import React from 'react';
import PropTypes from 'prop-types';

import { PrimaryButtonSto, TextButtonSto } from '.';

class ModalSto extends React.Component {
    focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    focusableContent;
    firstFocusableElement;
    lastFocusableElement;

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyboard);
        this.focusOnModal();
    }

    componentDidUpdate() {
        this.focusOnModal();
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleTab);
    }

    focusOnModal = () => {
        if (this.props.visible) {
            this.focusableContent = this.modalRef.querySelectorAll(this.focusableElements);
            if (this.focusableContent.length) {
                this.firstFocusableElement = this.focusableContent[0];
                this.lastFocusableElement = this.focusableContent[this.focusableContent.length - 1];
                this.firstFocusableElement.focus();
            }
        }
    };

    setModalRef = (node) => {
        this.modalRef = node;
    };

    /**
     * This method is used to trap focus in the modal to make it AODA compliant. It keeps the user from being able to tab
     * through elements outside of the modal and allows the user to escape out of it using the esc key.
     * @param event
     */
    handleKeyboard = (event) => {
        if (this.modalRef) {
            let isTabPressed = event.key === 'Tab';
            let isEscPressed = event.key === 'Escape';
            if (isEscPressed) {
                this.onOk();
            }
            if (!isTabPressed) {
                return;
            }
            // for shift + tab combination
            if (event.shiftKey && document.activeElement === this.firstFocusableElement) {
                this.focusItem(event, this.lastFocusableElement);
            }
            // if focus has reached to last focusable element then the focus restarts back on the first focusable element
            if (document.activeElement === this.lastFocusableElement) {
                this.focusItem(event, this.firstFocusableElement);
            }
        }
    };

    /**
     * Apply focus to an element
     * @param event
     * @param element
     */
    focusItem = (event, element) => {
        element.focus();
        event.preventDefault();
    };

    onOk = (e) => {
        this.props.onOk && this.props.onOk(e);
    };

    onCancel = (e) => {
        this.props.onCancel && this.props.onCancel(e);
    };

    renderButtons() {
        let cancelButton = null;
        let okButton = null;

        if (this.props.cancelText) {
            cancelButton = (
                <div className="sto-links bottom">
                    <TextButtonSto
                        id="cancel-link"
                        labelKey={this.props.cancelText}
                        onClick={this.onCancel}
                        size={this.props.buttonSize}
                    />
                </div>
            );
        }
        if (this.props.okText) {
            const buttonType = this.props.isCTA ? 'cta' : 'button';
            okButton = (
                <div className="button-sto-container">
                    <PrimaryButtonSto
                        disabled={this.props.okDisabled}
                        id={`page-${this.props.modalType}-modal-${buttonType}`}
                        labelKey={this.props.okText}
                        onClick={this.onOk}
                        size={this.props.buttonSize}
                    />
                </div>
            );
        }
        return (
            <div className="modal-sto-buttons">
                {cancelButton}
                {okButton}
            </div>
        );
    }

    render() {
        if (!this.props.visible) {
            return null;
        } else {
            return (
                <div
                    aria-modal={true}
                    className={`modal-sto-mask ${this.props.visible && 'visible'} ${this.props.wrapClassName}`}
                    id="sto-modal"
                    ref={this.setModalRef}
                    role="dialog"
                >
                    <div className="modal-sto">
                        <div className="modal-sto-body">
                            {this.props.children}
                            {this.renderButtons()}
                        </div>
                    </div>
                </div>
            );
        }
    }
}

ModalSto.propTypes = {
    buttonSize: PropTypes.oneOf(['small', 'medium', 'large']),
    cancelText: PropTypes.string,
    children: PropTypes.node,
    isCTA: PropTypes.bool,
    mask: PropTypes.bool,
    maskClosable: PropTypes.bool,
    modalType: PropTypes.string,
    okDisabled: PropTypes.bool,
    okText: PropTypes.string,
    onCancel: PropTypes.func,
    onOk: PropTypes.func,
    visible: PropTypes.bool,
    wrapClassName: PropTypes.string,
};

ModalSto.defaultProps = {
    buttonSize: 'medium',
    mask: true,
    maskClosable: true,
    okDisabled: false,
    okText: 'ok',
    visible: true,
    wrapClassName: '',
};

export default ModalSto;
